/**
 * The record shapes the gateway speaks, in the Firestore vocabulary both
 * store backends keep (`pageList`, `boxConfiguration`, `evaluationStatus`,
 * `matchedPage`, `result`). Ported from the Flutter app's `lib/models/`,
 * which is 2,308 lines of hand-written JSON parsing — here the JSON *is*
 * the type, and only the handful of shapes the gateway renames on the wire
 * get a mapping function (see `lib/api/templates.ts`).
 *
 * `openapi.d.ts` beside this file is generated from
 * `../api/docs/openapi.yaml` (`npm run gen:api`) and is the source of truth
 * for request and response envelopes. These are the domain records inside
 * them, which the spec largely describes inline.
 */

/**
 * A box's kind, as the gateway stores it.
 *
 * **`text` and `textDetection` are the same kind.** Both spellings occur in
 * `box_configurations` — the gateway folds them together in `typeFamily`
 * (`internal/templates/fields.go`) and accepts either on write. A client map
 * keyed on this union therefore has to carry both, or a `text` box reads as
 * `undefined`: that is how the editor came to label a selected box
 * "Strasse · undefined" and the cockpit to report 0 Text fields on a template
 * full of them.
 */
export type FieldType = 'textDetection' | 'text' | 'checkBox' | 'signature' | 'radioBox'

export type TextType =
  | 'text'
  | 'date'
  | 'number'
  | 'email'
  | 'phone'
  | 'iban'
  | 'postcode'
  | 'url'
  | 'id'

/** A box's rectangle, in the template page image's own pixels. */
export interface Position {
  x: number
  y: number
  width: number
  height: number
}

export interface RadioBoxChild {
  id: string
  label: string
  position?: Position
}

export interface BoxConfiguration {
  id: string
  label: string
  type: FieldType
  textType?: TextType
  position?: Position
  radioBoxChildren?: RadioBoxChild[]
  /** The shared `template_fields` row this box binds to. Null on templates
   *  older than migration 00006 and on Firestore templates. */
  fieldId?: string | null
}

/** Async PDF-to-image conversion state for a page added after upload. */
export type PageStatus = 'ready' | 'converting' | 'failed'

export interface SinglePage {
  imagePath: string
  page: number
  uploadedAt: number
  boxConfiguration: BoxConfiguration[]
  /** The page number this page is an alternative layout for. Null means it
   *  is a primary page. Only one level deep: a page with variants cannot
   *  itself be a variant. */
  variantOf?: number | null
  /** The variant's display name, e.g. "2018". */
  label?: string | null
  status: PageStatus
}

/** A template-level field shared across the template's layout variants. */
export interface TemplateField {
  id: string
  name: string
  type: FieldType
  textType?: TextType
  ordinal: number
}

export interface AccessControl {
  read: string[]
  write: string[]
  admin: string[]
  owner?: string
}

export interface LlmConsent {
  approver?: string
  date?: string
  provider?: string
  region?: string
}

export interface Template {
  id: string
  title: string
  imagePath: string
  status?: string
  folder?: string
  numPages: number
  pageList: SinglePage[]
  accessControl: AccessControl
  fields: TemplateField[]
  confidenceThreshold: number
  /** Only the list endpoint reports these; 0 / undefined elsewhere. */
  documentCount: number
  fieldCount?: number
  trainingAllowed: boolean
  llmConsent?: LlmConsent | null
  llmModel: string
  recordSchema?: Record<string, unknown> | null
  createdAt?: number
  updatedAt?: number
  /** RFC3339, present only on an archived template (both keys are
   *  `omitempty` on the wire). */
  archivedAt?: string
  /** The **uid** of whoever archived it, not a name: resolve it through the
   *  access list, or show the date alone. Absent for every template archived
   *  before the column existed — which is not "archived by the creator", so
   *  do not fall back to one. */
  archivedBy?: string
}

/**
 * Page numbers are never compacted — they are referenced by `matchedPage` on
 * documents, `variantOf` on other pages and the classifier's cache key — so
 * a deleted page leaves a gap and `page` is not an index into `pageList`.
 */
export function pageByNumber(template: Template, page: number): SinglePage | undefined {
  return template.pageList.find((p) => p.page === page)
}

export function pageIndexByNumber(template: Template, page: number): number {
  return template.pageList.findIndex((p) => p.page === page)
}

/** What to show for a layout: its label, else "Seite n". */
export function pageDisplayName(page: SinglePage): string {
  return page.label && page.label.length > 0 ? page.label : `Seite ${page.page}`
}

// -- Field detection -------------------------------------------------------

/** The template status the gateway sets while field detection runs in the
 *  converter — for a PDF after its pages are rendered, for an image right
 *  after the upload. The pages are ready but carry no boxes yet. */
export const TEMPLATE_DETECTING = 'detecting'

/** Field detection is still running: the pages show, the boxes are coming. */
export function templateDetecting(template: Template): boolean {
  return template.status === TEMPLATE_DETECTING
}

/** Not finished yet: pages still being rendered, or rendered but still
 *  being searched for fields. A screen that shows the detected boxes polls
 *  while this holds — reading the boxes any earlier reports "no fields". */
export function templateSettling(template: Template | undefined): boolean {
  if (!template) return true
  // The template's own `converting` counts as much as a page's: the
  // converter inserts each rendered page as `ready` while the template stays
  // `converting` until the last one is done, so a poll inside a render sees
  // ready pages without boxes.
  return (
    template.pageList.length === 0 ||
    template.pageList.some((p) => p.status === 'converting') ||
    template.status === 'converting' ||
    templateDetecting(template)
  )
}

export type DetectionMethod = 'cv' | 'llm'

/** One page's worth of detected boxes, as the editor receives them. */
export interface PageSuggestions {
  page: number
  boxes: FieldSuggestion[]
  /** Set when this one page failed while others succeeded. */
  error?: string
  imageSize?: { width: number; height: number }
}

export interface FieldSuggestion {
  id: string
  label: string
  type: FieldType
  textType?: TextType
  position: Position
  /** True when the label matched one of the template's existing shared
   *  fields across every layout variant — this is what keeps a new layout's
   *  "Vorname" on the same export column as the old one's. */
  matchedField?: boolean
  fieldId?: string | null
  confidence?: number
}

export interface FieldSuggestions {
  method: DetectionMethod
  pages: PageSuggestions[]
  totalFields: number
  /** The model's note about the page, when the llm detector ran. */
  remark?: string
}

/**
 * What upload-time detection found, returned by
 * `POST /v1/templates?detectMethod=…`. A PDF has no pages at upload time,
 * so its detection runs in the converter and the upload answers
 * `pending: true`.
 */
export interface DetectionResult {
  method: DetectionMethod
  totalFields: number
  remark?: string
  pages?: PageSuggestions[]
  pending?: boolean
  /** Detection is never fatal: the template is stored either way and this
   *  says what went wrong. */
  error?: string
}

// -- Auth ------------------------------------------------------------------

/**
 * The signed-in person, after normalisation.
 *
 * `GET /v1/auth/me` answers with the gateway's `models.User` — `uid`, a
 * single `name`, `role`, `is_active` — while the profile endpoints speak
 * `id`/`firstName`/`displayName`. Both shapes reach the client, so
 * `normalizeUser` in `lib/api/auth.ts` accepts either. Getting this wrong
 * on the Flutter side made `getMe()` return null and left the app stuck on
 * "signed out" with a perfectly good session.
 */
export interface UserProfile {
  id: string
  email: string
  displayName: string
  firstName?: string
  lastName?: string
  role?: 'admin' | 'user'
  isActive?: boolean
  emailVerified?: boolean
  onboarded?: boolean
  defaultFolder?: string
  profilePicture?: string
  folders?: string[]
  recentDocuments?: string[]
}

export interface AuthSession {
  access_token: string
  user?: UserProfile
}

/** Who is looking through this session, when it is a staff member viewing
 *  as someone else (`POST /v1/staff/users/:uid/impersonate` →
 *  `POST /v1/auth/impersonation/exchange`). */
export interface ImpersonatedBy {
  uid: string
  email: string
  name?: string
}

/**
 * The one-time code's payoff: a 30-minute, read-only session with no
 * refresh token and no cookie — everything `AuthProvider.beginImpersonation`
 * needs to stand the session up without going anywhere near the normal
 * login/refresh machinery.
 */
export interface ImpersonationSession {
  accessToken: string
  expiresIn: number
  expiresAt: string
  user: UserProfile
  impersonatedBy: ImpersonatedBy
}

// -- Deployment configuration ----------------------------------------------

/**
 * What this deployment allows an upload to be — `GET /v1/config`.
 *
 * `maxSizeMb` is the server's own number, so the client keeps no constant of
 * its own: a deployment that raises the limit raises it everywhere, and one
 * that lowers it no longer lets the browser start an upload the gateway will
 * refuse.
 */
export interface UploadConfig {
  maxSizeMb: number
  /** MIME types, as the gateway names them. */
  accepts: string[]
}

/**
 * The e-mail-verification gate, as it stands for the caller.
 *
 * `enabled: false` is the whole answer: the gate is off, and nothing about
 * verification belongs on screen — not even an "unbestätigt" hint derived
 * from `emailVerified`, which is reported for other reasons.
 *
 * `uploadsPausedAt` is the deadline the gateway itself computes
 * (`created_at + graceHours`, the same instant `enforceVerification` uses).
 * It is resolved server-side on purpose: a client that recomputes it from
 * `graceHours` is a second implementation of one policy and goes wrong the
 * day the policy stops being "account age". Render it; never compute it.
 * Absent means there is nothing to announce — the address is confirmed, or
 * the account's start is unknown, which the gateway treats as "the clock
 * cannot have run out".
 */
export interface VerificationConfig {
  enabled: boolean
  /** Descriptive only, for the copy ("24 Stunden"). Not for arithmetic. */
  graceHours?: number
  /** Only present with a user in context. */
  emailVerified?: boolean
  /** RFC3339. Only when `emailVerified === false` and the start is known. */
  uploadsPausedAt?: string
}

export interface ClientConfig {
  upload: UploadConfig
  verification: VerificationConfig
  /** `enabled: false` means this deployment applies no plan limits at all —
   *  invoiced work only. No remaining-pages UI, and `GET /v1/billing/usage`
   *  answers `{plan: "unlimited", enforced: false}` rather than inventing
   *  limits nobody enforces. */
  billing: { enabled: boolean }
  /** The model vendor this deployment reads documents with. `available:
   *  false` means no model will read anything and no consent can be
   *  recorded — a screen must then not name one. Taken from the server so
   *  the sentence that asks for consent cannot drift from what actually
   *  runs. */
  ai: { available: boolean; provider?: string; region?: string }
}

// -- Public upload links ---------------------------------------------------

export interface UploadLinkInfo {
  templateTitle: string
  accepts: string[]
  maxSizeMb: number
}

export interface PublicUploadResult {
  documentId: string
  jobId?: string
}

// -- Evaluation documents --------------------------------------------------

export type EvaluationStatus =
  | 'neu'
  | 'inProgress'
  | 'problematic'
  | 'complete'
  | 'noMatch'
  | 'other'

/**
 * One field as `GET /v1/templates/:id/results` reports it — a projection
 * built by `formatField` in the gateway, with review precedence already
 * applied server-side into `value`.
 *
 * This is NOT the shape of `EvaluationDocument.result`; that one is
 * `DocumentResultField` below, and every review, records and funnel-result
 * screen reads that one. Confusing the two is what this file got wrong
 * before, so the two names are kept apart deliberately.
 *
 * `needsReview` absent means the field was never judged — not that it passed.
 */
export interface ResultField {
  type: 'text' | 'checkBox' | 'signature'
  value: string
  confidence: number
  reviewed: boolean
  needsReview?: boolean
  reviewReason?: string
  fieldId?: string
  visionText?: string
  secondOpinion?: string
  agreement?: boolean
  stage?: string
  stages?: string[]
}

export interface MatchMetrics {
  inliers?: number
  coverage?: number
  similarity?: number
  ambiguous?: boolean
  runnerUp?: { page: number; similarity: number }
  forcedPage?: number
  manual?: boolean
  manualCorners?: number[][]
  reasons?: string[]
  dewarp?: Record<string, unknown>
}

export interface EvaluationDocument {
  id: string
  title: string
  evaluationStatus: EvaluationStatus
  matchedPage?: number | null
  needsReview?: boolean
  /** How many fields the analyzer flagged, and which. Zero and absent on
   *  documents analysed before the flag existed — not "checked, and fine". */
  needsReviewCount?: number
  needsReviewFields?: string[]
  /** The stored analysis, keyed by **box id** (`boxConfiguration[].id`), not
   *  by label and not by `fieldId`. A chip list therefore joins against the
   *  template's `boxConfiguration` for the label. */
  result?: Record<string, DocumentResultField>
  /** The record check's output, in the template's `recordSchema` shape. */
  record?: Record<string, unknown>
  /** What produced the values shown — "OCR", "KI-Bild", "KI-Bild + geprüft".
   *  A second axis to `evaluationStatus`, which stays a workflow state.
   *  Empty on documents analysed before stages were stamped; render it
   *  through `documentStageLabel`. */
  stage?: string
  /** Microseconds since the epoch, as the worker writes them — not
   *  milliseconds, and not the `uploaded_at` RFC3339 field the same payload
   *  also carries under its snake_case name. */
  uploadedAt?: number
  evaluatedAt?: number
  /** Pages of the uploaded file. A multi-page upload produces several
   *  analysed documents under one `originalDocumentId`, each holding only
   *  its own page's fields. */
  pages?: number
  /** The analysis failure message, when the status is failed. */
  error?: string
  matchMetrics?: MatchMetrics
  /** A German label for the first entry of `matchMetrics.reasons`. */
  noMatchReason?: string
  llmRemark?: string
  /** The file as it was uploaded: photographed at an angle, at its own
   *  resolution, never straightened. A result field's coordinates do **not**
   *  belong to this frame. */
  imagePath?: string
  /** The annotated page the worker stores (`documents/{templateId}/analyzed/…`)
   *  and the enhanced one beside it, both the scan warped into the template's
   *  frame — the 2400px frame `result` coordinates are in. The wire names,
   *  from `models.DocumentListItem`. */
  evalImagePath?: string
  enhancedImagePath?: string
  originalDocumentId?: string
  page?: number
  createdAt?: number
  updatedAt?: number
}

export interface DocumentCounts {
  total: number
  byStatus: Partial<Record<EvaluationStatus, number>>
  needsReview?: number
  /** The flagged **fields** behind `needsReview`'s flagged documents, so a
   *  cockpit can say "Unklar — 5 Felder in 2 Dokumenten". The gateway omits
   *  the key when the aggregate fails, so undefined means "not counted",
   *  never zero. */
  needsReviewFields?: number
  /** Keyed by matched template page, for the cockpit's layout cards and the
   *  page-delete confirmation. `noMatch` documents are omitted. */
  byMatchedPage?: Record<string, number>
}

export type JobState = 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled'

export interface JobStatus {
  job_id: string
  status: JobState
  /** Percent, 0-100, as the worker writes it (`worker.py update_job_status`).
   *  The only progress figure `GET /v1/jobs/:jobId` carries: there is no
   *  processed-page count and no page total in that response. */
  progress?: number
  error?: string
  template_id?: string
  created_at?: string
  updated_at?: string
}

// -- The stored analysis of one document -----------------------------------

/** One person's judgement of one field, appended by
 *  `POST …/documents/:documentId/fields/:fieldId/review`. Never overwrites
 *  `text`: the raw analyzer output stays inspectable. */
export interface FieldReview {
  accepted: boolean
  correction?: string
  reviewedBy: string
  reviewedByName?: string
  /** RFC3339, as the gateway writes it. */
  reviewedAt?: string
}

/**
 * One field of `EvaluationDocument.result` — the stored analysis, written by
 * `src/core/persist.py` and passed through the gateway unchanged (see
 * `api/internal/api/fields.go`, which parses the same keys).
 *
 * Distinct from `ResultField`, which is the results endpoint's projection.
 * Coordinates are plain x/y/x2/y2 in the deskewed page image's pixel space
 * (the 2400px working frame), the same numbers the stored analyzed and
 * enhanced page images are drawn with — so a box overlay on either image
 * uses them directly, while the template's `Position` is in the template
 * page's own, larger frame.
 */
export interface DocumentResultField {
  x: number
  y: number
  x2: number
  y2: number
  /** What the recognizer read. Never rewritten by a review. */
  text: string
  confidence: number
  /**
   * The reviewed value. Present only once someone has reviewed the field,
   * and then authoritative **even when empty** — an empty `finalText` on a
   * reviewed field is the reviewer saying the field is blank. Read it
   * through `resultFieldValue`, never as `finalText || text`.
   */
  finalText?: string
  reviews?: FieldReview[]
  /** Set when the analyzer judged the field; absent means nobody judged it. */
  needsReview?: boolean
  /** The analyzer's own words on why, e.g. "leer, aber Tinte im Feld". Raw
   *  diagnostic text, not display copy — the two reasons a client can render
   *  itself come from `resultFieldFlagReason`. */
  reviewReason?: string | null
  /** The recognizer's reading, kept when a model's reading became the value
   *  (`LLM_PASS_MODE=primary`). */
  visionText?: string
  /** The other engine's reading of the same field, and how far the two
   *  agree (0..1). Written by the LLM page pass. */
  secondOpinion?: string
  agreement?: number | null
  /** A completion the model derived from the rest of the page. Offered for
   *  confirmation, never the delivered value. */
  suggestion?: string
  /** The model's own note on why it was unsure about this field. */
  modelNote?: string
  /** The ladder this value climbed, and its label for delivery. Absent on
   *  results written before stages existed. */
  stages?: RecognitionArm[]
  stage?: string
  /** Crop diagnostics from `src/core/refine.py`: residual alignment shift and
   *  the share of the writable area carrying ink. */
  shift?: [number, number]
  ink?: number
}

/**
 * The delivered value of one field: the reviewed text once someone has
 * reviewed it — **including when that text is empty** — and the recognizer's
 * text otherwise.
 *
 * This mirrors `fieldResult.value()` and `getFieldValue` in the gateway
 * exactly. A reviewer who cleared a field that OCR misread means the field is
 * blank; falling back to `text` would resurrect on screen the value they
 * removed, and the correction would look like it never happened.
 */
export function resultFieldValue(field?: DocumentResultField | null): string {
  if (!field) return ''
  if (isResultFieldReviewed(field) && typeof field.finalText === 'string') {
    return field.finalText
  }
  return field.text ?? ''
}

/** Whether a person has judged this field. One review is enough; the last
 *  one is the one that stands. */
export function isResultFieldReviewed(field?: DocumentResultField | null): boolean {
  return (field?.reviews?.length ?? 0) > 0
}

/** The review that stands, i.e. the most recent one. */
export function lastFieldReview(field?: DocumentResultField | null): FieldReview | undefined {
  const reviews = field?.reviews
  return reviews && reviews.length > 0 ? reviews[reviews.length - 1] : undefined
}

export type ResultFieldFlagKind = 'empty' | 'lowConfidence'

export interface ResultFieldFlag {
  kind: ResultFieldFlagKind
  /** German, ready to render. */
  label: string
  /** Present on `lowConfidence`: the confidence as a percentage, rounded. */
  percent?: number
}

/**
 * Why a field is waiting for a person, in the two terms the data actually
 * supports: nothing was recognised, or the recognizer was not confident
 * enough for this template's `confidenceThreshold` (0..1, default 0.8).
 *
 * Deliberately no richer than that. The stored `reviewReason` and
 * `matchMetrics` carry more, but nothing maps them onto trustworthy display
 * copy, and a plausible-sounding invented reason is worse than a plain one.
 * A reviewed field is never flagged: a person has already decided.
 */
export function resultFieldFlagReason(
  field: DocumentResultField | undefined | null,
  threshold: number,
): ResultFieldFlag | null {
  if (isResultFieldReviewed(field)) return null

  // An empty field is only a question when the worker says it is.
  //
  // `fieldtext.decide` already judged this with something the client cannot
  // see: `ink`, the share of the field's writable area carrying ink. A blank
  // reading on a box with ink in it means the recognizer missed something and
  // is flagged; a blank reading on a box with no ink means the person left it
  // blank, which is an answer, not an uncertainty.
  //
  // Flagging every empty field here overrode that and buried the real ones.
  // On a passport application whose entire "Bitte nicht ausfüllen — for
  // office use only" block is legitimately empty, it turned a clean read into
  // "20 unklar" and asked somebody to confirm a dozen boxes nobody had filled
  // in.
  if (resultFieldValue(field).trim() === '') {
    // `needsReview` absent means the field was never judged, so nothing is
    // known about it and the question stands.
    const judged = field?.needsReview !== undefined
    if (judged && !field?.needsReview) return null
    return { kind: 'empty', label: 'Kein Wert erkannt' }
  }
  const confidence = field?.confidence ?? 0
  if (confidence < threshold) {
    const percent = Math.round(confidence * 100)
    return { kind: 'lowConfidence', label: `Niedrige Sicherheit · ${percent} %`, percent }
  }
  return null
}

// -- Recognition arms and the stage ladder ---------------------------------

/**
 * The ways one document can be read, stored side by side rather than
 * overwriting each other (`document_variants`). `ocr` is the baseline
 * snapshot, `human` the reviewed values, `llm_web` is planned and not
 * runnable.
 */
export type RecognitionArm = 'ocr' | 'llm_text' | 'llm_only' | 'llm_image' | 'llm_web' | 'human'

/** The arms a job may be started with (`models.RunnableArms`); anything else
 *  is a 400. `ocr` and `human` are snapshots, not runs. */
export const RUNNABLE_ARMS: RecognitionArm[] = ['llm_text', 'llm_image', 'llm_only']

/** Mirrors `STAGE_ORDER` in `src/core/arms.py`: the ladder's rungs, weakest
 *  first, with `human` last because review sits on top of any machine stage. */
export const STAGE_ORDER: RecognitionArm[] = [
  'ocr',
  'llm_text',
  'llm_only',
  'llm_image',
  'llm_web',
  'human',
]

/** Mirrors `STAGE_TITLES` in `src/core/arms.py` — the worker composes the
 *  same strings into `stage`, so these must not drift. */
export const STAGE_TITLES: Record<RecognitionArm, string> = {
  ocr: 'OCR',
  llm_text: 'KI-Text',
  llm_only: 'KI-Bild ohne OCR',
  llm_image: 'KI-Bild',
  llm_web: 'KI-Websuche',
  human: 'geprüft',
}

/** What a document or field shows when no stage was ever stamped on it. */
export const STAGE_LABEL_NONE = '–'

/** A document being analysed right now. Not a stored stage: the worker
 *  stamps `stage` only once it has written a result, so a document in
 *  progress has none and the list would otherwise show a dash for the one
 *  state the person is waiting on. */
export const STAGE_LABEL_RUNNING = 'OCR läuft'

/**
 * A value's provenance: the last machine stage it climbed, plus the review
 * if there was one — "OCR", "KI-Bild", "KI-Bild + geprüft". Mirrors
 * `arms.stage_label`, for the variants endpoint, whose fields carry `stages`
 * but whose own label a client may want to recompute.
 */
export function stageLabel(stages: Iterable<RecognitionArm | string>): string {
  const seen = new Set(stages)
  const climbed = STAGE_ORDER.filter((s) => seen.has(s))
  if (climbed.length === 0) return ''
  const machine = climbed.filter((s) => s !== 'human')
  const parts: string[] = []
  if (machine.length > 0) parts.push(STAGE_TITLES[machine[machine.length - 1]])
  if (seen.has('human')) parts.push(STAGE_TITLES.human)
  return parts.join(' + ')
}

/** The "Stufe" column: the document's own stage, the running state while it
 *  is being analysed, and a dash when neither applies. */
export function documentStageLabel(doc: EvaluationDocument): string {
  if (doc.stage && doc.stage.length > 0) return doc.stage
  if (doc.evaluationStatus === 'inProgress') return STAGE_LABEL_RUNNING
  return STAGE_LABEL_NONE
}

/** One arm's reading of one document — `GET …/documents/:documentId/variants`.
 *  `result` has the same shape as the document's own, so the review UI renders
 *  a variant with the renderer it already has. */
export interface DocumentVariant {
  arm: RecognitionArm | string
  /** Present from migration 00016 on: a variant is keyed (document, arm,
   *  model), so two models on one arm no longer overwrite each other. */
  model?: string
  result?: Record<string, DocumentResultField>
  record?: Record<string, unknown>
  needsReview?: boolean
  needsReviewCount: number
  needsReviewFields?: string[]
  inputTokens?: number
  outputTokens?: number
  durationMs?: number
  meta?: Record<string, unknown>
  createdAt?: string
}

/** One arm's coverage over a template's documents — `GET /v1/templates/:id/arms`.
 *  `changedFields` / `changedDocuments` are measured against the delivered
 *  values: the numbers the promote decision turns on. */
export interface ArmSummary {
  arm: RecognitionArm | string
  model?: string
  /** The provenance label this reading would stamp once promoted. */
  stage?: string
  documents: number
  needsReview: number
  inputTokens: number
  outputTokens: number
  cost: number
  /** False when the model is not in the price table: show "unbekannt", not 0. */
  priced: boolean
  fields: number
  changedFields: number
  changedDocuments: number
  /** The reading the documents currently carry. */
  delivered: boolean
  /**
   * True when another arm on the same model may be billed for the same
   * calls — currently the page-pass family (`llm_image`, `llm_only`,
   * `llm_web`), which share one `purpose` in the audit trail. `cost`,
   * `inputTokens` and `outputTokens` are then an upper bound, not this
   * arm's bill, and want a qualifier ("≤", "geteilt") beside them.
   */
  costShared?: boolean
}

// -- Jobs ------------------------------------------------------------------

/**
 * One analysis run over a set of documents.
 *
 * `src/admin/types/job.ts` declared this first, for the admin build alone,
 * and its `JobStatus` union omits `cancelled` — a state of its own, not a
 * failure, which the template job list shows. This is the canonical one;
 * the admin module should re-export from here rather than keep a second.
 */
export interface Job {
  id: string
  template_id: string
  document_ids: string[]
  status: JobState
  /** 0-100. */
  progress: number
  created_at: string
  started_at?: string
  completed_at?: string
  error?: string
  result_path?: string
  /** Set on a run started for one recognition arm rather than an ordinary
   *  analysis. */
  arm?: RecognitionArm | string
  model?: string
  /** A manual match override: a 1-based template page number. */
  force_page?: number
  /** The form's corners on the scan, as fractions of its width and height in
   *  the order TL, TR, BR, BL. */
  manual_corners?: number[][]
  /**
   * Whether the arm's reading becomes the delivered values. **The worker
   * defaults this to true**, so a comparison run has to send `false`
   * explicitly or it rewrites what is delivered — and the next run then
   * reads its own output as the OCR baseline.
   */
  apply?: boolean
}

export interface JobList {
  jobs: Job[]
  total: number
  limit: number
  offset: number
  has_more: boolean
}

// -- Upload links ----------------------------------------------------------

/** A shareable link that lets a phone upload into one template without
 *  signing in. `url` is built from the gateway's configured public base, so
 *  a locally created link may point at production. */
export interface UploadLink {
  token: string
  url?: string
  createdAt: string
  expiresAt?: string | null
  revokedAt?: string | null
  uploads: number
}

// -- Sharing ---------------------------------------------------------------

export type AccessRole = 'read' | 'write' | 'admin'

/** One person's access to a template. `source` says where it comes from:
 *  a direct entry on the template, or membership of its folder — only a
 *  `template` entry can be removed here. */
export interface TemplateAccessEntry {
  userId: string
  email?: string
  name?: string
  role: AccessRole
  source: 'template' | 'folder'
  folderId?: string
  owner?: boolean
}

export interface TemplateAccess {
  entries: TemplateAccessEntry[]
  /** Whether the caller may change this list at all — template admins and
   *  owners, not only system admins. */
  canManage: boolean
}

export interface UserLookupResult {
  uid: string
  email: string
  name: string
}

// -- LLM usage, models and cost -------------------------------------------

/** What one call cost, in US dollars. `priced: false` means the model is not
 *  in the price table: unknown, never free. */
export interface LlmCost {
  inputUsd: number
  outputUsd: number
  cacheWriteUsd: number
  cacheReadUsd: number
  totalUsd: number
  priced: boolean
}

/**
 * One row of the LLM audit trail — every call, failed ones included.
 *
 * The cache counts are separate because providers report cached input
 * *outside* `input_tokens` and at their own rates. Rows written before
 * migration 00012 carry 0, which means not recorded, not "no cache".
 */
export interface LlmCall {
  id: string
  templateId: string
  /** Absent for a call not about one document, e.g. field detection. */
  documentId?: string
  /** "llm_pass", "record_check", "field_detect", "arm_llm_image", … */
  purpose: string
  provider: string
  region: string
  model: string
  inputTokens: number
  outputTokens: number
  cacheWriteTokens: number
  cacheReadTokens: number
  cost: LlmCost
  ok: boolean
  error?: string
  createdAt: string
}

/** Totals over the returned calls, overall and per purpose. */
export interface LlmUsageGroup {
  purpose?: string
  calls: number
  failed: number
  inputTokens: number
  outputTokens: number
  cacheWriteTokens: number
  cacheReadTokens: number
  cost: LlmCost
  unpricedCalls: number
  models?: string[]
}

export interface LlmUsageSummary {
  total: LlmUsageGroup
  groups: LlmUsageGroup[]
  /** The listing hit its limit, so the summary speaks for a slice. */
  truncated?: boolean
}

/** USD per million tokens, the four rates a call is billed at. */
export interface TokenRates {
  Input: number
  Output: number
  CacheWrite: number
  CacheRead: number
}

/** A model the picker may offer — the price table's keys, so every choice is
 *  one whose run can be costed. The provider travels with it but is not
 *  choosable: which vendor may see a template's pages is what its
 *  `llmConsent` records. */
export interface LlmModel {
  id: string
  provider: 'anthropic' | 'google' | string
  rates: TokenRates
}

// -- Plans, credits and packages -------------------------------------------

/** `GET /v1/billing/usage`. On a deployment with billing off the answer is
 *  `{plan: "unlimited", enforced: false}` and `usage` is absent, rather than
 *  limits the gateway will not apply. */
export interface BillingUsage {
  plan: string
  pages: number
  maxPages: number
  templates: number
  maxTemplates: number
  retentionDays: number
  /** Omitted entirely on an unlimited plan, so "none left" is distinguishable
   *  from "no limit" without reading `maxPages`. */
  pagesRemaining?: number
}

export interface BillingUsageResponse {
  plan: string
  enforced: boolean
  usage?: BillingUsage
}

/** One box of credits offered self-service. An invoiced-only deployment
 *  answers with an empty list and `forSale: false`. */
export interface Package {
  key: string
  priceId: string
}

export interface PackagesResponse {
  packages: Package[]
  forSale: boolean
  balance?: number
  /** Credits per page by operation ("standard", "praezision", …). */
  rates?: Record<string, number>
}
